import { t } from "@travily/i18n";
import type { DialogSize } from "@ui/components/Dialog";
import { Dialog } from "@ui/components/Dialog";
import { Icon } from "@ui/components/Icon/Icon";
import { Button } from "@ui/primitives/Button";
import { composeRenderProps } from "@ui/primitives/shared";
import { type ReactNode, useState } from "react";
import { searchBarPillButton } from "./searchBarPillButton";

export interface SearchBarFiltersProps {
  /** Optional press handler. The Filters dialog opens automatically regardless. */
  onPress?: () => void;
  /** Controlled open state. Defaults to internal state. */
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  /** Dialog body content. Falls back to a placeholder when omitted. */
  children?: ReactNode;
  /** Desktop dialog width. Defaults to "sm". */
  size?: DialogSize;
  /** Optional sticky footer (e.g. Clear all + Apply). Receives a `close` callback. */
  footer?: (close: () => void) => ReactNode;
}

export function SearchBarFilters({
  onPress,
  isOpen,
  onOpenChange,
  children,
  size = "sm",
  footer,
}: SearchBarFiltersProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = isOpen !== undefined;
  const open = isControlled ? isOpen : internalOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };
  const styles = searchBarPillButton();
  return (
    <>
      <Button
        className={composeRenderProps("ml-4 w-24", (className, renderProps) =>
          styles.base({ ...renderProps, className }),
        )}
        onPress={() => {
          onPress?.();
          setOpen(true);
        }}
      >
        <span aria-hidden="true" className={styles.icon()}>
          <Icon name="filter" />
        </span>
        <span className={styles.label()}>{t("components.search_bar.filters_label")}</span>
      </Button>
      <Dialog.Root onOpenChange={setOpen} isOpen={open} size={size}>
        <Dialog.Content>
          <Dialog.Header onClose={() => setOpen(false)} title={t("components.search_bar.filters_title")} />
          <Dialog.Body>{children ?? <div aria-hidden="true" className="min-h-40" />}</Dialog.Body>
          {footer && <Dialog.Footer>{footer(() => setOpen(false))}</Dialog.Footer>}
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}
